import {
  IonContent,
  IonPage,
  IonSkeletonText,
  IonIcon,
  IonLabel,
  IonCard,
  IonItem,
  IonList,
  IonBadge,
  IonImg,
  useIonAlert,
  IonHeader,
  IonToolbar,
  IonButton,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  useIonModal,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  useIonToast,
  IonSearchbar,
  IonFab,
  IonFabButton,
  IonAvatar,
  IonChip,
  IonRow,
  IonCol,
  IonGrid,
} from "@ionic/react";
import React, { useState } from "react";
import {
  storefrontOutline,
  arrowBackOutline,
  personCircleOutline,
  timeOutline,
  chevronDownCircleOutline,
  addOutline,
  warningOutline,
  callOutline,
  chatbubbleOutline,
  filterOutline,
  trendingUpOutline,
  calendarOutline,
  helpCircleOutline,
} from "ionicons/icons";
import { RestApi } from "../services/Api";
import Detail from "../components/customer-follow-up/Detail";
import Form from "../components/customer-follow-up/Form";
import { loadList } from "../models/Data";
import { useTranslation } from "react-i18next";
import { getNameDate, getRoleMenu, useConstructor } from "../services/Library";
import { useSelector } from "react-redux";
import "./CustomerFollowUp.scss";

const CustomerFollowUp: React.FC = () => {
  const [presentToast] = useIonToast();
  const { t } = useTranslation(["general"]);
  const [alert] = useIonAlert();
  const user = useSelector((state: any) => state.user.value);
  const getRole = getRoleMenu(user.role_menu, "customer_follow_up");
  const [isInfiniteDisabled, setInfiniteDisabled] = useState(false);
  const [getIdCustomerFollowUp, setGetIdCustomerFollowUp] = useState<Number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [loaded, setLoaded] = useState<boolean>(false);
  const [listCustomerFollowUp, setListCustomerFollowUp] = useState<any>([]);
  const [dataCustomerFollowUp, setDataCustomerFollowUp] = useState<any>([]);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const statusOptions: any = {
    interested: {
      name: t("status_interested"),
      color: "#48bb78",
      bgColor: "#f0fff4",
      icon: trendingUpOutline,
    },
    negotiation: {
      name: t("status_negotiation"),
      color: "#ecc94b",
      bgColor: "#fefcbf",
      icon: calendarOutline,
    },
    waiting: {
      name: t("status_waiting"),
      color: "#a0aec0",
      bgColor: "#f7fafc",
      icon: helpCircleOutline,
    }
  };

  const contactMethodIcons: any = {
    call: callOutline,
    massage: chatbubbleOutline,
  };

  const handleDismissDetail = () => {
    dismissDetail();
    getCustomerFollowUpList(
      `customer-follow-up?page=1&search=${searchText}&status=${filterStatus}`,
      false,
    );
  };

  const handleDismissForm = () => {
    dismissForm();
    getCustomerFollowUpList(
      `customer-follow-up?page=1&search=${searchText}&status=${filterStatus}`,
      false,
    );
  };

  const [presentDetail, dismissDetail] = useIonModal(Detail, {
    idCustomerFollowUp: getIdCustomerFollowUp,
    type: null,
    onDismiss: handleDismissDetail,
  });

  const [presentForm, dismissForm] = useIonModal(Form, {
    idCustomerFollowUp: getIdCustomerFollowUp,
    type: "create",
    onDismiss: handleDismissForm,
  });

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [{ cssClass: "alert-button-confirm", text: "Ok" }],
    });
  };

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    getCustomerFollowUpList(
      `customer-follow-up?page=1&search=${searchText}&status=${filterStatus}`,
      false,
    );
    event.detail.complete();
  };

  const getCustomerFollowUpList = async (page: string, next: boolean) => {
    await RestApi(page, "get", {}, "application/json")
      .then((res: any) => {
        if (res.data.status) {
          setDataCustomerFollowUp(res.data.data);
          if (next) {
            setListCustomerFollowUp((prevState: any) => [
              ...prevState,
              ...res.data.data.data,
            ]);
          } else {
            setListCustomerFollowUp(res.data.data.data);
          }
        } else {
          setListCustomerFollowUp([]);
          setDataCustomerFollowUp([]);
          presentToast({
            message: res.data.message,
            duration: 2500,
            color: "danger",
            icon: warningOutline,
          });
        }
        setLoaded(true);
      })
      .catch((err: any) => {
        massageAlert("error", err.message);
        setLoaded(true);
      });
  };

  const loadData = (ev: any) => {
    if (dataCustomerFollowUp.current_page === dataCustomerFollowUp.last_page) {
      setInfiniteDisabled(true);
      return;
    }
    getCustomerFollowUpList(
      `customer-follow-up?page=${dataCustomerFollowUp.current_page + 1}&search=${searchText}&status=${filterStatus}`,
      true,
    );
    ev.target.complete();
  };

  const handleSearch = (e: any) => {
    setSearchText(e.detail.value!);
    getCustomerFollowUpList(
      `customer-follow-up?page=1&search=${e.detail.value!}&status=${filterStatus}`,
      false,
    );
  };

  const applyFilter = (type: string, value: string) => {
    if (type === "status") {
      setFilterStatus(value);
    } 
    getCustomerFollowUpList(
      `customer-follow-up?page=1&search=${searchText}&status=${type === "status" ? value : filterStatus}`,
      false,
    );
  };

  const clearFilters = () => {
    setFilterStatus("");
    getCustomerFollowUpList(
      `customer-follow-up?page=1&search=${searchText}&status=`,
      false,
    );
  };

  useConstructor(() => {
    getCustomerFollowUpList(
      `customer-follow-up?page=1&search=${searchText}&status=${filterStatus}`,
      false,
    );
  });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="modern-toolbar">
          <IonButton
            slot="start"
            fill="clear"
            routerLink="/home"
            routerDirection="back"
            className="back-button"
          >
            <IonIcon icon={arrowBackOutline} />
          </IonButton>
          <div className="search-container">
            <IonSearchbar
              value={searchText}
              onIonInput={handleSearch}
              placeholder={`${t("search")} ${t("module.customer-follow-up")}`}
              className="modern-searchbar"
              animated
            />
          </div>
          <IonButton
            fill="clear"
            slot="end"
            className={`filter-button ${filterStatus ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <IonIcon icon={filterOutline} />
            {filterStatus && (
              <span className="filter-dot"></span>
            )}
          </IonButton>
        </IonToolbar>
        {showFilters && (
          <div className="filters-panel">
            <IonGrid className="filters-grid">
              <IonRow>
                <IonCol size="12">
                  <p className="filters-title">{t("status")}</p>
                  <div className="filter-chips">
                    {Object.entries(statusOptions).map(
                      ([key, value]: [string, any]) => (
                        <IonChip
                          key={key}
                          onClick={() => applyFilter("status", key)}
                          className={filterStatus === key ? "active-chip" : ""}
                          style={
                            {
                              "--background":
                                filterStatus === key
                                  ? value.bgColor
                                  : "#f7fafc",
                              "--color":
                                filterStatus === key ? value.color : "#4a5568",
                            } as any
                          }
                        >
                          <IonIcon
                            icon={value.icon}
                            style={{ color: value.color }}
                          />
                          <IonLabel>{value.name}</IonLabel>
                        </IonChip>
                      ),
                    )}
                  </div>
                </IonCol>
              </IonRow>
              {filterStatus && (
                <IonRow>
                  <IonCol size="12">
                    <IonButton
                      fill="clear"
                      size="small"
                      onClick={clearFilters}
                      className="clear-filters"
                    >
                      Clear filters
                    </IonButton>
                  </IonCol>
                </IonRow>
              )}
            </IonGrid>
          </div>
        )}
      </IonHeader>
      <IonContent className="modern-content">
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t("loading") ?? ""}
            refreshingSpinner="circles"
            refreshingText={t("loading") ?? ""}
          />
        </IonRefresher>
        {!loaded && (
          <IonList className="skeleton-list">
            {loadList.map((_, i) => (
              <IonCard key={i} className="skeleton-card">
                <IonItem lines="none">
                  <IonAvatar slot="start">
                    <IonSkeletonText animated />
                  </IonAvatar>
                  <IonLabel>
                    <IonSkeletonText
                      animated
                      style={{ width: "60%", height: "20px" }}
                    />
                    <IonSkeletonText
                      animated
                      style={{ width: "40%", height: "16px" }}
                    />
                  </IonLabel>
                </IonItem>
              </IonCard>
            ))}
          </IonList>
        )}
        {listCustomerFollowUp.length > 0 && loaded ? (
          <>
            <IonGrid className="cards-grid">
              {listCustomerFollowUp.map((value: any, key: any) => (
                <IonCol size="12" sizeMd="6" sizeLg="4" key={key}>
                  <IonCard
                    className="modern-card"
                    onClick={() => {
                      setGetIdCustomerFollowUp(value.id);
                      presentDetail({ cssClass: "modal-fullscreen" });
                    }}
                  >
                    <div className="card-header">
                      <IonRow className="ion-no-padding ion-no-margin">
                        <IonCol
                          size="10"
                          className="ion-no-padding ion-no-margin"
                        >
                          <div className="customer-info">
                            <div className="customer-avatar">
                              <IonIcon icon={storefrontOutline} />
                            </div>
                            <div className="customer-details">
                              <h3 className="customer-name">
                                {value.customer?.customer_name || "N/A"}
                              </h3>
                              <span className="customer-id">
                                ID: {value.customer_id}
                              </span>
                            </div>
                          </div>
                        </IonCol>
                        <IonCol
                          size="2"
                          style={{
                            marginLeft: "auto",
                          }}
                        >
                          <IonBadge
                            color="light"
                            className="ion-float-end"
                            style={{
                              float: "right",
                              backgroundColor:
                                statusOptions[value.status]?.bgColor ||
                                "#f7fafc",
                              color:
                                statusOptions[value.status]?.color || "#4a5568",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontWeight: 600,
                              fontSize: "0.95em",
                              padding: "7px 14px",
                              borderRadius: "18px",
                            }}
                          >
                            <IonIcon
                              icon={
                                statusOptions[value.status]?.icon ||
                                helpCircleOutline
                              }
                              style={{ marginRight: "6px", fontSize: "17px" }}
                            />
                            <span>
                              {statusOptions[value.status]?.name ||
                                value.status}
                            </span>
                          </IonBadge>
                        </IonCol>
                      </IonRow>
                    </div>

                    <div className="card-content">
                      <div className="info-row">
                        <IonIcon
                          icon={personCircleOutline}
                          className="info-icon"
                        />
                        <span>{value.updated_by ?? value.created_by}</span>
                        <div className="follow-up-date">
                          <IonIcon icon={timeOutline} className="info-icon" />
                          <span>{getNameDate(value.follow_up_date)}</span>
                        </div>
                      </div>

                      <div className="info-row">
                        <IonIcon
                          icon={
                            contactMethodIcons[value.contact_method] ||
                            callOutline
                          }
                          className="info-icon"
                        />
                        <span>{t(value.contact_method)}</span>
                        
                      </div>
                      {value.notes && (
                        <div className="notes-preview">
                          <p>
                            {value.notes.length > 80
                              ? value.notes.substring(0, 80) + "..."
                              : value.notes}
                          </p>
                        </div>
                      )}

                      {value.next_follow_up && (
                        <div className="next-follow-up">
                          <IonIcon icon={calendarOutline} />
                          <span>Next: {getNameDate(value.next_follow_up)}</span>
                        </div>
                      )}
                    </div>
                  </IonCard>
                </IonCol>
              ))}
            </IonGrid>
            <IonInfiniteScroll
              onIonInfinite={loadData}
              threshold="100px"
              disabled={isInfiniteDisabled}
            >
              <IonInfiniteScrollContent
                loadingSpinner="bubbles"
                loadingText="Loading more..."
              />
            </IonInfiniteScroll>
          </>
        ) : (
          loaded && (
            <div className="empty-state">
              <IonImg
                src="assets/img/search-empty.png"
                className="empty-image"
              />
              <h3>No customers found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          )
        )}
        {getRole?.role_menu_add === 1 && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton
              onClick={() => {
                setGetIdCustomerFollowUp(0);
                presentForm({ cssClass: "modal-fullscreen" });
              }}
              className="modern-fab"
              size="small"
            >
              <IonIcon icon={addOutline} />
            </IonFabButton>
          </IonFab>
        )}
      </IonContent>
    </IonPage>
  );
};

export default CustomerFollowUp;
